import React, { CSSProperties, useEffect } from "react";
import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import useFiltersQuery from "common/Filters/common/useFiltersQuery";
import {
  FiltersActions,
  FiltersSelector,
  WorkspaceType,
  LanguageNameSpaces,
} from "@shared/types";
import { useLocation } from "react-router-dom";
import { selectWorkspace } from "@shared/slices/common.slice";
import { useSelector } from "react-redux";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import { useAdminGroupsSelect } from "features/admin/hooks";
import useMedia from "utils/hooks/useMedia";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { SizeType } from "antd/es/config-provider/SizeContext";
import styled, { useTheme } from "styled-components";
import FiltersSelect from "common/Filters/common/FiltersSelect.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface GroupSelectProps {
  filtersSelectors?: FiltersSelector;
  filtersActions?: FiltersActions;
  style?: CSSProperties;
  size?: SizeType;
}

const GroupSelect = ({
  filtersSelectors = groupConfigurationsFilters.selectors,
  filtersActions = groupConfigurationsFilters.actions,
  style,
  size,
}: GroupSelectProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const location = useLocation();
  useFiltersQuery(filtersSelectors, filtersActions);
  const { updateUrl, groupsFilterSectionData, querySubGroupId } =
    useAdminGroupsSelect({
      filtersSelectors,
      filtersActions,
    });
  const maxDropdownWidth = useMedia([`(max-width: 400px)`], [300], 375);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();

  const [filtersSubGroupId] = groupsFilterSectionData.value;
  const workspace = useSelector(selectWorkspace);
  const isAdmin = workspace === WorkspaceType.ADMIN;
  const options = groupsFilterSectionData?.options as unknown as
    | number[]
    | undefined;
  const showValue = !!options?.find((option) => option === filtersSubGroupId);

  useEffect(() => {
    if (filtersSubGroupId && isAdmin && !querySubGroupId) {
      updateUrl(filtersSubGroupId);
    }
  }, [location.pathname, filtersSubGroupId, isAdmin]);

  const getGroupFilterItem = (option: number) => {
    const imageSize = 22;
    return (
      <Flex
        $justifyContent="flex-start"
        style={{ marginRight: isBottomNavigator ? 0 : 18 }}
      >
        <Flex
          style={{
            height: imageSize,
            width: imageSize,
            marginLeft: isBottomNavigator ? 4 : 0,
            marginRight: isBottomNavigator ? 0 : 8,
            flex: "none",
          }}
        >
          <IconWithFallback
            {...groupsFilterSectionData.displayData?.getIconProps?.(option)}
            iconSize={isBottomNavigator ? "l" : "xxl"}
            iconStyle={{
              borderRadius: 4,
            }}
          />
        </Flex>
        <GroupName>
          {groupsFilterSectionData.displayData?.getDisplay?.(option)}
        </GroupName>
      </Flex>
    );
  };

  // New look: an empty picker next to the page title reads as broken; the
  // page's own empty state explains that there are no groups.
  if (isNewLook && options?.length === 0) return null;

  const parsedOptions = options?.map((option) => {
    return {
      value: option,
      searchValue: groupsFilterSectionData.displayData?.getDisplay?.(option),
      label: getGroupFilterItem(option),
    };
  });

  return (
    <FiltersSelect
      style={{ maxWidth: 350, ...style }}
      value={showValue ? filtersSubGroupId : undefined}
      onChange={(groupId: number) => {
        if (isAdmin) {
          updateUrl(groupId);
        }
        groupsFilterSectionData.onChange([groupId]);
      }}
      popupMatchSelectWidth={false}
      styles={{
        popup: {
          root: {
            maxWidth: maxDropdownWidth,
          },
        },
      }}
      optionLabelProp="label"
      size={size ?? "large"}
      showSearch={{ optionFilterProp: "searchValue" }}
      options={parsedOptions}
      mobileTitle={t("groupSelect.mobileTitle")}
      $singleRow={isBottomNavigator}
      $isBottomNavigator={isBottomNavigator}
      hideArrow={isBottomNavigator}
    />
  );
};

export default GroupSelect;

const GroupName = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;
`;
